import { ChevronDown, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { selectTenant, useAppSelector } from '@/store'
import { DateType } from '@/utils/date'
import ScrollButton, { ScrollPosition } from './ScrollButton'
import TreeNode from './TreeNode'

// Types
type Month = {
  id: number
  label: string
}

type Quarter = {
  id: number
  label: string
  months: Month[]
}

type Year = {
  year: number
  quarters: Quarter[]
}

type Selection = {
  type: 'year' | 'quarter' | 'month' | null
  year?: number
  quarter?: number
  month?: number
}

interface BasicDatePickerProps {
  // Old interface (for backward compatibility)
  setDate?: React.Dispatch<React.SetStateAction<DateRange | undefined>>
  setDateFilterType?: React.Dispatch<React.SetStateAction<DateType>>

  // New interface (for pending state management)
  value?: DateRange | undefined
  dateFilterType?: DateType
  onChange?: (
    newDate: DateRange | undefined,
    newDateFilterType: DateType
  ) => void

  type?: 'pnl' | 'spend'
  noMonths?: boolean
  yearsUnselectable?: boolean // When true, years cannot be selected individually (only quarters)
}

// Constants
const QUARTERS: Quarter[] = [
  {
    id: 1,
    label: 'Q1',
    months: [
      { id: 0, label: 'January' },
      { id: 1, label: 'February' },
      { id: 2, label: 'March' },
    ],
  },
  {
    id: 2,
    label: 'Q2',
    months: [
      { id: 3, label: 'April' },
      { id: 4, label: 'May' },
      { id: 5, label: 'June' },
    ],
  },
  {
    id: 3,
    label: 'Q3',
    months: [
      { id: 6, label: 'July' },
      { id: 7, label: 'August' },
      { id: 8, label: 'September' },
    ],
  },
  {
    id: 4,
    label: 'Q4',
    months: [
      { id: 9, label: 'October' },
      { id: 10, label: 'November' },
      { id: 11, label: 'December' },
    ],
  },
]

// Utility functions
const generateYears = (startYear: number = 2018, tenant?: string): Year[] => {
  const currentYear = new Date().getFullYear()
  // Show full year range for all tenants (blankstreet default is 2025 Q3 but all years/quarters are selectable)
  const endYear = currentYear
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => ({
      year: endYear - i,
      quarters: QUARTERS,
    })
  )

  // For dev tenant, exclude years 2025 and 2026
  if (tenant === 'dev') {
    return years.filter((y) => y.year !== 2025 && y.year !== 2026)
  }

  return years
}

const getDefaultSelection = (type?: 'pnl' | 'spend', tenant?: string) => {
  // const currentDate = new Date()
  if (type === 'spend') {
    // For spend: default to Q1 2024
    return {
      type: 'quarter' as const,
      year: 2024,
      month: 2, // March (0-based index) - last month of Q1
      quarter: 1, // Q1
    }
  }

  // For blankstreet: default to 2025 Q3 (July–September)
  if (tenant === 'blankstreet') {
    return {
      type: 'quarter' as const,
      year: 2025,
      month: 8, // September (0-based) - last month of Q3
      quarter: 3, // Q3
    }
  }

  // For PnL or default: Q1 2025, but Q1 2024 for dev tenant
  const defaultYear = tenant === 'dev' ? 2024 : 2025
  return {
    type: 'quarter' as const,
    // year: currentDate.getFullYear(),
    // quarter: Math.floor((currentDate.getMonth() - 1) / 3) + 1,
    // month: (currentDate.getMonth() - 1 + 12) % 12,
    year: defaultYear,
    month: 2, // March (0-based index) - last month of Q1
    quarter: 1, // Q1
  }
}

// Main component
export const BasicDatePicker = ({
  setDate,
  setDateFilterType,
  value,
  dateFilterType: externalDateFilterType,
  onChange,
  type,
  noMonths = false,
  yearsUnselectable = false,
}: BasicDatePickerProps) => {
  const tenant = useAppSelector(selectTenant)

  const years = useMemo(() => generateYears(2018, tenant), [tenant])

  const isNewInterface = onChange !== undefined
  const initialSelection =
    isNewInterface && noMonths
      ? { type: null }
      : getDefaultSelection(type, tenant)

  const [expandedYear, setExpandedYear] = useState<number | null>(
    initialSelection.type === null ? null : (initialSelection.year ?? null)
  )
  const [expandedQuarter, setExpandedQuarter] = useState<number | null>(
    initialSelection.type === null ? null : (initialSelection.quarter ?? null)
  )
  const [selection, setSelection] = useState<Selection>(initialSelection)
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    isAtTop: true,
    isAtBottom: false,
  })
  const hasAppliedInitialSelection = useRef(false)

  const deriveSelectionFromDateRange = useCallback(
    (
      dateRange: DateRange | undefined,
      dateFilterType?: DateType
    ): Selection => {
      if (!dateRange?.from) {
        return noMonths ? { type: null } : getDefaultSelection(type, tenant)
      }

      const year = dateRange.from.getFullYear()
      const month = dateRange.from.getMonth()
      const quarter = Math.floor(month / 3) + 1

      if (dateFilterType === 'yearly') {
        return { type: 'year', year }
      } else if (dateFilterType === 'quarterly') {
        return { type: 'quarter', year, quarter }
      } else if (dateFilterType === 'monthly') {
        return { type: 'month', year, quarter, month }
      }

      return noMonths ? { type: null } : getDefaultSelection(type, tenant)
    },
    [type, tenant, noMonths]
  )

  // Sync internal selection with external value when it changes
  useEffect(() => {
    if (isNewInterface) {
      const newSelection = deriveSelectionFromDateRange(
        value,
        externalDateFilterType
      )
      setSelection(newSelection)
      setExpandedYear(newSelection.year ?? null)
      setExpandedQuarter(newSelection.quarter ?? null)
    }
  }, [
    value,
    externalDateFilterType,
    isNewInterface,
    deriveSelectionFromDateRange,
  ])

  const isDateSelectable = useCallback(
    (year: number) => {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()

      // For dev tenant, explicitly disable year 2025
      if (tenant === 'dev' && year === 2025) {
        return false
      }

      // For blankstreet: same as others — all years up to current are selectable (default is 2025 Q3)
      if (tenant === 'blankstreet') {
        return year <= currentYear
      }

      // Only allow years that are less than or equal to current year
      return year <= currentYear
    },
    [tenant]
  )

  const isQuarterSelectable = useCallback(
    (year: number, quarter: number) => {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1

      // For dev tenant, explicitly disable year 2025
      if (tenant === 'dev' && year === 2025) {
        return false
      }

      // For blankstreet: all quarters selectable like others (default is 2025 Q3)
      if (tenant === 'blankstreet') {
        if (year < currentYear) return true
        if (year === currentYear) return quarter <= currentQuarter
        return false
      }

      // If year is less than current year, all quarters are selectable
      if (year < currentYear) return true

      // If year is current year, only quarters up to current quarter are selectable
      if (year === currentYear) return quarter <= currentQuarter

      // Future years are not selectable
      return false
    },
    [tenant]
  )

  const isMonthSelectable = useCallback(
    (year: number, month: number) => {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()

      // For dev tenant, explicitly disable year 2025
      if (tenant === 'dev' && year === 2025) {
        return false
      }

      // For blankstreet: all months selectable like others (default is 2025 Q3)
      if (tenant === 'blankstreet') {
        if (year < currentYear) return true
        if (year === currentYear) return month <= currentMonth
        return false
      }

      // If year is less than current year, all months are selectable
      if (year < currentYear) return true

      // If year is current year, only months up to current month are selectable
      if (year === currentYear) return month <= currentMonth

      // Future years are not selectable
      return false
    },
    [tenant]
  )

  const isYearSelected = useCallback(
    (year: number) => selection.type === 'year' && selection.year === year,
    [selection]
  )

  const isQuarterSelected = useCallback(
    (year: number, quarter: number) =>
      selection.type === 'quarter' &&
      selection.year === year &&
      selection.quarter === quarter,
    [selection]
  )

  const isMonthSelected = useCallback(
    (year: number, quarter: number, month: number) =>
      selection.type === 'month' &&
      selection.year === year &&
      selection.quarter === quarter &&
      selection.month === month,
    [selection]
  )

  const updateSelection = useCallback(
    (newSelection: Selection) => {
      try {
        setSelection(newSelection)

        if (!newSelection.type || newSelection.year === undefined) {
          if (isNewInterface && onChange) {
            onChange(undefined, 'monthly')
          } else if (setDate) {
            setDate(undefined)
          }
          return
        }

        const currentDate = new Date()
        const currentYear = currentDate.getFullYear()
        const currentMonth = currentDate.getMonth()

        // Handle year selection
        if (newSelection.type === 'year') {
          const startDate = new Date(newSelection.year, 0, 1)
          const endDate =
            newSelection.year === currentYear
              ? new Date(currentYear, currentMonth, 0) // Up to end of previous month for current year
              : new Date(newSelection.year, 11, 31) // Full year for past years

          const dateRange = { from: startDate, to: endDate }
          const dateType: DateType = 'yearly'

          if (isNewInterface && onChange) {
            onChange(dateRange, dateType)
          } else {
            setDateFilterType?.(dateType)
            setDate?.(dateRange)
          }
          return
        }

        // Handle quarter selection
        if (
          newSelection.type === 'quarter' &&
          newSelection.quarter !== undefined
        ) {
          const currentQuarter = Math.floor(currentMonth / 3) + 1
          const startMonth = newSelection.quarter * 3 - 3

          const startDate = new Date(newSelection.year, startMonth, 1)
          const endDate =
            newSelection.year === currentYear &&
            newSelection.quarter === currentQuarter
              ? new Date(currentYear, currentMonth, 0) // Up to end of previous month for current quarter
              : new Date(newSelection.year, startMonth + 3, 0) // Full quarter for past quarters

          const dateRange = { from: startDate, to: endDate }
          const dateType: DateType = 'quarterly'

          if (isNewInterface && onChange) {
            onChange(dateRange, dateType)
          } else {
            setDateFilterType?.(dateType)
            setDate?.(dateRange)
          }
          return
        }

        // Handle month selection
        if (newSelection.type === 'month' && newSelection.month !== undefined) {
          const startDate = new Date(newSelection.year, newSelection.month, 1)
          const endDate = new Date(newSelection.year, newSelection.month + 1, 0)

          const dateRange = { from: startDate, to: endDate }
          const dateType: DateType = 'monthly'

          if (isNewInterface && onChange) {
            onChange(dateRange, dateType)
          } else {
            setDateFilterType?.(dateType)
            setDate?.(dateRange)
          }
          return
        }
      } catch (error) {
        console.error('Error updating selection:', error)
      }
    },
    [setDate, setDateFilterType, isNewInterface, onChange]
  )

  // Apply initial selection to parent component (only for old interface)
  useEffect(() => {
    if (!hasAppliedInitialSelection.current && !isNewInterface) {
      const initialSelection = getDefaultSelection(type, tenant)
      updateSelection(initialSelection)
      hasAppliedInitialSelection.current = true
    }
  }, [type, updateSelection, isNewInterface, tenant])

  const handleYearSelect = useCallback(
    (year: number, checked: boolean) => {
      if (!isDateSelectable(year) || yearsUnselectable) return

      updateSelection(
        checked
          ? { type: 'year', year }
          : noMonths
            ? { type: null }
            : getDefaultSelection(type, tenant)
      )
    },
    [
      isDateSelectable,
      updateSelection,
      type,
      tenant,
      noMonths,
      yearsUnselectable,
    ]
  )

  const handleQuarterSelect = useCallback(
    (year: number, quarter: number, checked: boolean) => {
      if (!isDateSelectable(year) || !isQuarterSelectable(year, quarter)) return

      updateSelection(
        checked
          ? { type: 'quarter', year, quarter }
          : noMonths
            ? { type: null }
            : getDefaultSelection(type, tenant)
      )
    },
    [
      isDateSelectable,
      isQuarterSelectable,
      updateSelection,
      type,
      tenant,
      noMonths,
    ]
  )

  const handleMonthSelect = useCallback(
    (year: number, quarter: number, month: number, checked: boolean) => {
      if (!isDateSelectable(year) || !isMonthSelectable(year, month)) return

      updateSelection(
        checked
          ? { type: 'month', year, quarter, month }
          : getDefaultSelection(type, tenant)
      )
    },
    [isDateSelectable, isMonthSelectable, updateSelection, type, tenant]
  )

  const getSelectionText = useCallback(() => {
    if (!selection.type) return 'Select Date'

    switch (selection.type) {
      case 'year':
        return `Year ${selection.year}`
      case 'quarter':
        return `${selection.year} Q${selection.quarter}`
      case 'month': {
        const monthName = years
          .find((y) => y.year === selection.year)
          ?.quarters.find((q) => q.id === selection.quarter)
          ?.months.find((m) => m.id === selection.month)?.label
        return monthName ? `${monthName} ${selection.year}` : 'Select Date'
      }
      default:
        return 'Select Date'
    }
  }, [selection, years])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isAtTop = scrollTop === 0
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1
    setScrollPosition({ isAtTop, isAtBottom })
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      setScrollPosition({ isAtTop: true, isAtBottom: false })
      const container = document.querySelector('.picker-content')
      if (container) {
        container.scrollTop = 0
      }
    }
  }, [])

  const handleScrollUp = useCallback(() => {
    const container = document.querySelector('.picker-content')
    if (container) {
      container.scrollTop -= 100
    }
  }, [])

  const handleScrollDown = useCallback(() => {
    const container = document.querySelector('.picker-content')
    if (container) {
      container.scrollTop += 100
    }
  }, [])

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`w-[200px] justify-start h-8 font-normal ${
            !selection.type ? 'text-muted-foreground' : ''
          }`}
        >
          <span className="truncate">{getSelectionText()}</span>
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="h-[250px] flex flex-col">
          {!scrollPosition.isAtTop && (
            <ScrollButton direction="up" onClick={handleScrollUp} />
          )}
          <div
            className="flex-1 p-2 overflow-y-auto picker-content [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onScroll={handleScroll}
          >
            {years.map((yearData) => (
              <div key={yearData.year} className="mb-1">
                <TreeNode
                  icon={
                    expandedYear === yearData.year ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )
                  }
                  label={yearData.year.toString()}
                  checked={
                    yearsUnselectable ? false : isYearSelected(yearData.year)
                  }
                  disabled={!isDateSelectable(yearData.year)}
                  onCheck={(checked) => {
                    if (!yearsUnselectable) {
                      handleYearSelect(yearData.year, checked)
                    }
                  }}
                  onClick={() => {
                    if (isDateSelectable(yearData.year)) {
                      setExpandedYear(
                        expandedYear === yearData.year ? null : yearData.year
                      )
                    }
                  }}
                  className="text-sm"
                  type="date"
                  branchOnly={yearsUnselectable}
                />

                {expandedYear === yearData.year && (
                  <div className="ml-4">
                    {yearData.quarters.map((quarter) => (
                      <div key={quarter.id}>
                        <TreeNode
                          icon={
                            noMonths ? null : expandedQuarter === quarter.id ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )
                          }
                          label={quarter.label}
                          checked={isQuarterSelected(yearData.year, quarter.id)}
                          disabled={
                            !isDateSelectable(yearData.year) ||
                            !isQuarterSelectable(yearData.year, quarter.id)
                          }
                          onCheck={(checked) =>
                            handleQuarterSelect(
                              yearData.year,
                              quarter.id,
                              checked
                            )
                          }
                          onClick={() => {
                            if (
                              noMonths ||
                              !isDateSelectable(yearData.year) ||
                              !isQuarterSelectable(yearData.year, quarter.id)
                            ) {
                              return
                            }
                            setExpandedQuarter(
                              expandedQuarter === quarter.id ? null : quarter.id
                            )
                          }}
                          className="mt-1 text-sm"
                          type="date"
                        />

                        {!noMonths && expandedQuarter === quarter.id && (
                          <div className="ml-12">
                            {quarter.months.map((month) => (
                              <TreeNode
                                key={month.id}
                                icon={null}
                                label={month.label}
                                checked={isMonthSelected(
                                  yearData.year,
                                  quarter.id,
                                  month.id
                                )}
                                disabled={
                                  !isDateSelectable(yearData.year) ||
                                  !isMonthSelectable(yearData.year, month.id)
                                }
                                onCheck={(checked) =>
                                  handleMonthSelect(
                                    yearData.year,
                                    quarter.id,
                                    month.id,
                                    checked
                                  )
                                }
                                onClick={() => {}}
                                className="mt-1 text-sm"
                                type="date"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {!scrollPosition.isAtBottom && (
            <ScrollButton direction="down" onClick={handleScrollDown} />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
